/**
 * A list of points read a page at a time: an instrument's points in the gateway drawer
 * (components/iot/InstrumentValues) and a gateway's own tags in its detail sheet. The page size, which points a page
 * shows, in words, the batched latest read keyed by tag, and how long ago a reading arrived. Also one point's edit:
 * poll interval, deadband, heartbeat and whether it is read (the platform's ranges and rules, and the PATCH body).
 * Pure.
 */

import { errorDetailOf, statusOf, writeErrorMessage } from "@/lib/api/error";
import type { TagUpdateBody } from "@/lib/api/twin-bindings";

/** Points on one page. */
export const POINTS_PAGE_SIZE = 50;

/** The longest poll interval a point takes, in seconds (a day). */
export const POLL_SEC_MAX = 86400;

/** A point's poll interval as the platform takes it: whole seconds, 1 to a day (else 400 `invalid_poll_sec`). */
export function isPollSec(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= POLL_SEC_MAX;
}

/** What a page counts, singular and plural. */
export interface PointsNoun {
  one: string;
  many: string;
}

const POINT: PointsNoun = { one: "point", many: "points" };

/** Which points a page shows, in words: "1 to 50 of 396"; "8 points" when they all fit. */
export function pointsPageLabel(offset: number, shown: number, total: number, noun: PointsNoun = POINT): string {
  if (total <= 0) return `No ${noun.many}`;
  if (offset === 0 && shown >= total) return `${total} ${total === 1 ? noun.one : noun.many}`;
  return `${offset + 1} to ${offset + shown} of ${total}`;
}

/** A point's latest reading, as the batched read (`POST /v1/iot/tags:latest`) answers it; null when it never reported. */
export interface PointLatest {
  value: number | null;
  time: string | null;
  quality_flag?: string | null;
}

/** The batched read's answer by tag id. A tag missing from it has no reading yet. */
export function latestByTag<T extends PointLatest & { tag_id: string }>(items: T[]): Record<string, PointLatest> {
  return Object.fromEntries(items.map((it) => [it.tag_id, it]));
}

/**
 * How long ago a reading arrived, from its own time, at `now` (epoch ms; this moment when absent): "just now",
 * "4 min ago", "3 h ago", "2 d ago". Null with no time, or a never-reported point's zero time. A time a little ahead
 * of this clock (the gateway's clock runs ahead) is "just now".
 */
export function readingAge(iso: string | null | undefined, now: number = Date.now()): string | null {
  const at = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(at) || at <= 0 || !Number.isFinite(now)) return null;
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** The shortest heartbeat the editor takes, in seconds (a minute; the platform takes from 1). */
export const HEARTBEAT_SEC_MIN = 60;

/** The longest heartbeat a point takes, in seconds (a week). */
export const HEARTBEAT_SEC_MAX = 604800;

/** The point's own heartbeat in seconds, or null when it has none (the read's `max_silence_sec` is then the default). */
export function ownHeartbeatSec(t: { max_silence_sec?: number | null; max_silence_own?: boolean }): number | null {
  return t.max_silence_own && t.max_silence_sec != null && t.max_silence_sec > 0 ? t.max_silence_sec : null;
}

/**
 * A point's effective heartbeat in seconds, by the platform's one rule: its own, else, with a deadband above 0,
 * `max(900, 6 × poll_sec)` (15 min up to a 150 s poll, an hour at 600 s); else none (null), since a point without a
 * deadband publishes every read anyway.
 */
export function effectiveHeartbeatSec(
  own: number | null | undefined,
  pollSec: number | null | undefined,
  deadband: number | null | undefined,
): number | null {
  if (own != null && own > 0) return own;
  if (deadband != null && deadband > 0) {
    // Without a poll interval of its own the point reads at the device's rate; the 900 s floor is its heartbeat.
    return pollSec != null && pollSec > 0 ? Math.max(900, 6 * pollSec) : 900;
  }
  return null;
}

/** A span of seconds, compact like the row's chips: "45 s", "15 min", "1.5 min", "6 h", "7 d". */
export function durationShort(sec: number): string {
  if (sec < 60) return `${sec} s`;
  if (sec < 3600 || sec % 3600 !== 0) return `${Math.round((sec / 60) * 10) / 10} min`;
  if (sec % 86400 === 0) return `${sec / 86400} d`;
  return `${sec / 3600} h`;
}

/** One point's poll interval, deadband, heartbeat and acquire, as `PATCH /v1/iot/tags/{id}` takes them. */
export type PointRateEdit = Pick<TagUpdateBody, "poll_sec" | "deadband" | "max_silence_sec" | "acquire">;

/** What a point's edit starts from: the tag as read. */
export interface PointRateCurrent {
  poll_sec?: number | null;
  deadband?: number | null;
  max_silence_sec?: number | null;
  max_silence_own?: boolean;
  acquire?: boolean;
}

/** One point's edit fields as typed. */
export interface PointRateForm {
  /** Seconds. */
  poll: string;
  deadband: string;
  /** Minutes; blank is the default. */
  heartbeat: string;
  /** Read this point. */
  acquire: boolean;
}

/** The edit fields a point opens with: its poll, its deadband, its own heartbeat in minutes (blank for the default). */
export function pointRateForm(t: PointRateCurrent): PointRateForm {
  const own = ownHeartbeatSec(t);
  return {
    poll: t.poll_sec != null ? String(t.poll_sec) : "",
    deadband: t.deadband != null && t.deadband > 0 ? String(t.deadband) : "",
    heartbeat: own != null ? String(Math.round((own / 60) * 100) / 100) : "",
    acquire: t.acquire !== false,
  };
}

/**
 * What a blank heartbeat means for the point as being edited (its typed poll and deadband when they read, else its
 * own), in words, and the default in minutes for the field's placeholder (null for none).
 */
export function heartbeatDefault(current: PointRateCurrent, form: PointRateForm): { hint: string; minutes: number | null } {
  const p = Number(form.poll.trim());
  const poll = form.poll.trim() !== "" && isPollSec(p) ? p : current.poll_sec;
  const d = form.deadband.trim() === "" ? 0 : Number(form.deadband.trim());
  const deadband = Number.isFinite(d) && d >= 0 ? d : current.deadband;
  const sec = effectiveHeartbeatSec(null, poll, deadband);
  if (sec == null) {
    return { hint: "A blank heartbeat is none: without a deadband the point publishes every read.", minutes: null };
  }
  return { hint: `A blank heartbeat is the default, ${durationShort(sec)}.`, minutes: Math.round((sec / 60) * 100) / 100 };
}

/**
 * A point's edit fields as typed → the PATCH body (only what changes, so an
 * untouched field is not sent), or what is wrong. Poll: whole seconds, 1 to
 * 86400; blank keeps the point's. Deadband: 0 or more; blank or 0 is none, sent
 * as 0 only to clear one the point has. Heartbeat: minutes, sent as whole
 * seconds 60 to 604800; blank is the default, sent as 0 only to clear the
 * point's own. Acquire: sent only when toggled.
 */
export function pointRateBody(current: PointRateCurrent, form: PointRateForm): { body: PointRateEdit } | { error: string } {
  const body: PointRateEdit = {};
  const p = form.poll.trim();
  if (p !== "") {
    const poll = Number(p);
    if (!isPollSec(poll)) return { error: `Poll: a whole number of seconds, 1 to ${POLL_SEC_MAX}.` };
    if (poll !== current.poll_sec) body.poll_sec = poll;
  }
  const d = form.deadband.trim();
  const deadband = d === "" ? 0 : Number(d);
  if (!Number.isFinite(deadband) || deadband < 0) return { error: "Deadband: a number, 0 or more." };
  if (deadband !== (current.deadband ?? 0)) body.deadband = deadband;
  const own = ownHeartbeatSec(current);
  const h = form.heartbeat.trim();
  if (h === "") {
    if (own != null) body.max_silence_sec = 0;
  } else {
    // Whole seconds; the point's own as it opened (even one under a minute, set elsewhere) is left alone.
    const sec = Math.round(Number(h) * 60);
    if (sec !== own) {
      if (!Number.isFinite(sec) || sec < HEARTBEAT_SEC_MIN || sec > HEARTBEAT_SEC_MAX) {
        return { error: `Heartbeat: minutes, 1 to ${HEARTBEAT_SEC_MAX / 60} (a week); blank for the default.` };
      }
      body.max_silence_sec = sec;
    }
  }
  if (form.acquire !== (current.acquire !== false)) body.acquire = form.acquire;
  return { body };
}

/**
 * A failed point save as one line. Changing a point needs the member role (a
 * technician gets 403); otherwise what the server said (an out-of-range value
 * is a 400 `invalid_poll_sec` / `invalid_deadband` / `invalid_max_silence_sec`
 * with its sentence).
 */
export function pointSaveError(err: unknown): string {
  if (statusOf(err) === 403) {
    const detail = errorDetailOf(err)?.replace(/[.\s]+$/, "");
    return detail ? `Needs the member role (${detail}).` : "Needs the member role.";
  }
  return writeErrorMessage(err, "Couldn't save the point.");
}
